import type { AuthMode } from '@/lib/schemas/auth';

/**
 * How the auth form should respond to a Supabase error.
 *
 * - `banned` — a soft-deleted account. Gets its own screen (`DeactivatedAccountCard`).
 * - `unconfirmed` — signed up but never clicked the link. Routes to the
 *   check-your-email screen, which owns the resend button.
 * - `inline` — everything else: render `message` in the form's error box, with
 *   `action` as the recovery route when there is one.
 */
export type AuthErrorKind = 'banned' | 'unconfirmed' | 'inline';

/** A recovery route offered beside the message — swaps the form's mode. */
export interface AuthErrorAction {
  label: string;
  mode: AuthMode;
}

export interface ClassifiedAuthError {
  kind: AuthErrorKind;
  message: string;
  action?: AuthErrorAction;
}

/**
 * One place that decides what a Supabase auth failure means.
 *
 * WHY THIS EXISTS. `useAuthSubmit` used to classify exactly one error shape -
 * `isBannedAuthError`, which is what earns a soft-deleted account its own
 * screen - and passed every other error's `message` straight through to the
 * form. That is how four cases users actually hit ended up rendering Supabase's
 * raw sentence with no route out:
 *
 *   - "Email not confirmed" printed as a dead end, even though
 *     `resendConfirmation` and the screen that offers it both already existed.
 *   - "For security purposes, you can only request this after 53 seconds" -
 *     the 429, most often reached by pressing Resend twice.
 *   - "User already registered" on sign-up, with no action at all, because
 *     `AuthForm` attached its recovery action only when `mode === 'login'`.
 *   - `TypeError: Failed to fetch` when offline. Supabase auth calls do not go
 *     through `lib/api/client.ts`, so they never emit `network-error` and
 *     neither banner covers them - and `OfflineBanner` deliberately hides
 *     itself on `/auth` on the assumption this layer handles it. It does now.
 *
 * `lib/account/restore-error.ts` is the same idea for the restore endpoint and
 * argues the case at length: letting a raw status string reach the user is a
 * defect, not a fallback.
 *
 * Codes are the real ones from `@supabase/auth-js`'s `error-codes.d.ts`. Each
 * is paired with a message regex because `code` is only populated on errors
 * that came back from an HTTP response - anything failing earlier has
 * `code === undefined`, and older clients did not send codes at all. That is
 * the same belt-and-braces the banned check has always used.
 */

const OFFLINE_MESSAGE =
  'You appear to be offline. Check your connection and try again.';

// Deliberately short: `AuthError` renders the action label inline, immediately
// after this sentence. Spelling the route out here as well ("Sign in instead,
// or reset the password...") made the rendered line stutter - the copy said it
// and then the button said it again.
const ALREADY_REGISTERED_MESSAGE = 'That email already has an account.';

const RATE_LIMIT_MESSAGE = 'Too many attempts. Wait a moment and try again.';

const read = (error: unknown, key: string): unknown =>
  error && typeof error === 'object'
    ? (error as Record<string, unknown>)[key]
    : undefined;

const codeOf = (error: unknown): string => {
  const code = read(error, 'code');
  return typeof code === 'string' ? code : '';
};

const statusOf = (error: unknown): number | undefined => {
  const status = read(error, 'status');
  return typeof status === 'number' ? status : undefined;
};

const messageOf = (error: unknown): string => {
  const message = read(error, 'message');
  return typeof message === 'string' && message
    ? message
    : 'Something went wrong. Please try again.';
};

/**
 * A banned sign-in error is how Supabase reports a soft-deleted account. It
 * arrives as a code containing "banned" on newer clients and as a message on
 * older ones, so both are checked.
 */
export const isBannedAuthError = (error: unknown): boolean =>
  codeOf(error).includes('banned') ||
  /banned|deactivated/i.test(messageOf(error));

/**
 * Offline, or the API unreachable.
 *
 * supabase-js wraps a failed fetch as `AuthRetryableFetchError`, which carries
 * no `code` and a status of 0, so `name` is the only stable discriminator.
 * `navigator.onLine` is checked too: it lies in the optimistic direction (it
 * can say online behind a captive portal) but a hard `false` is trustworthy,
 * and it catches the case where the browser rejected the request itself.
 */
const isOfflineError = (error: unknown): boolean => {
  if (read(error, 'name') === 'AuthRetryableFetchError') return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  return /failed to fetch|networkerror|network request failed/i.test(
    messageOf(error)
  );
};

/**
 * Supabase names the wait when it knows it ("...after 53 seconds"). Repeating
 * the number is the difference between a message someone can act on and one
 * that just says no.
 */
const rateLimitMessage = (raw: string): string => {
  const seconds = raw.match(/after (\d+) seconds?/i)?.[1];
  return seconds
    ? `Too many attempts. Try again in ${seconds} seconds.`
    : RATE_LIMIT_MESSAGE;
};

const isUnconfirmedError = (error: unknown): boolean =>
  codeOf(error) === 'email_not_confirmed' ||
  /email not confirmed/i.test(messageOf(error));

const isRateLimitError = (error: unknown): boolean => {
  const code = codeOf(error);
  return (
    code === 'over_email_send_rate_limit' ||
    code === 'over_request_rate_limit' ||
    statusOf(error) === 429
  );
};

const isAlreadyRegisteredError = (error: unknown): boolean => {
  const code = codeOf(error);
  return (
    code === 'user_already_exists' ||
    code === 'email_exists' ||
    /already registered|already exists/i.test(messageOf(error))
  );
};

export function classifyAuthError(
  error: unknown,
  mode: AuthMode
): ClassifiedAuthError {
  const message = messageOf(error);

  if (isBannedAuthError(error)) return { kind: 'banned', message };
  if (isUnconfirmedError(error)) return { kind: 'unconfirmed', message };

  // Before the rate-limit check: a transport failure is status 0, not 429.
  if (isOfflineError(error)) {
    return { kind: 'inline', message: OFFLINE_MESSAGE };
  }

  if (isRateLimitError(error)) {
    return { kind: 'inline', message: rateLimitMessage(message) };
  }

  if (isAlreadyRegisteredError(error)) {
    return {
      kind: 'inline',
      message: ALREADY_REGISTERED_MESSAGE,
      action: { label: 'Sign in instead', mode: 'login' },
    };
  }

  // Unrecognised: show Supabase's own wording rather than inventing copy for an
  // error we have not seen. Sign-in keeps its reset-password escape hatch,
  // which is what `AuthForm` used to hardcode - bad credentials are by far the
  // most common way to land here, and "I have forgotten it" is the way out.
  return {
    kind: 'inline',
    message,
    action:
      mode === 'login'
        ? { label: 'Reset your password', mode: 'reset' }
        : undefined,
  };
}
